import React from "react";
import StatusChip from "./StatusChip";
import { Utils } from "../../utils";
import { PAYMENT_STATUS, PAYMENT_STATUS_LABELS } from "../../utils/enums";

interface Props {
  row: any;
  perspective: "seller" | "buyer";
}

const PaymentStatusChip: React.FC<Props> = ({ row, perspective }) => {
  const rawStatus = row?.status || "";
  const isJoined = rawStatus === "basic_tx.joined";

  // Bank-transfer payments stay in `joined` until Trustap detects the
  // wire (1–3 business days). Honest yellow-warning labelling on both
  // sides — buyer needs the visual cue that funds haven't cleared yet,
  // seller mustn't treat it as confirmed income.
  if (isJoined && row?.paymentMethod === "bank_transfer") {
    return (
      <StatusChip
        className="statusChip--wide"
        tone="warning"
        icon="bi-bank"
        label="Awaiting bank transfer"
      />
    );
  }

  // Seller perspective only: buyer hasn't paid yet — surface it as a warning
  // so the seller doesn't treat this as confirmed income.
  if (perspective === "seller" && isJoined) {
    return (
      <StatusChip
        className="statusChip--wide"
        tone="warning"
        icon="bi-clock-fill"
        label="Awaiting payment"
      />
    );
  }

  // Complaint window (7 days). Seller sees when they can claim; buyer sees
  // how long they have to file a dispute.
  if (row?.isInComplaintWindow && row?.complaintWindowEndsAt) {
    const when = Utils.formatHoursFromNow(row.complaintWindowEndsAt);
    const subLabel =
      perspective === "seller" ? `Claim opens ${when}` : `Ends ${when}`;
    return (
      <StatusChip
        className="statusChip--wide"
        tone="warning"
        icon="bi-exclamation-triangle-fill"
        label="Complaint window"
        subLabel={subLabel}
      />
    );
  }

  // Once the seller has claimed, our /claim-transaction route stamps
  // `claimedAt` immediately and the funds-released webhook stamps
  // `payoutReleasedAt` — but `payment.status` can still read "delivered"
  // until Trustap's basic_tx.claimed / basic_tx.funds_released webhooks
  // land. Prefer the stamped timestamps so the chip reflects the real
  // state right away instead of showing "Delivered" (and, on the seller
  // side, alongside the now-hidden Claim action).
  const isReleased =
    !!row?.payoutReleasedAt || rawStatus === PAYMENT_STATUS.FUNDS_RELEASED;
  const isClaimed = !!row?.claimedAt;
  if (isReleased || isClaimed) {
    return (
      <StatusChip
        className="statusChip--wide"
        tone="success"
        icon="bi-check-circle-fill"
        capitalize
        label={
          isReleased
            ? PAYMENT_STATUS_LABELS[PAYMENT_STATUS.FUNDS_RELEASED]
            : PAYMENT_STATUS_LABELS[PAYMENT_STATUS.CLAIMED]
        }
      />
    );
  }

  // Prefer the shared user-facing label map so the chip and the filter
  // dropdown agree (e.g., "joined" → "Awaiting payment"). Fall back to a
  // prettified raw code for any unmapped state.
  const label =
    PAYMENT_STATUS_LABELS[rawStatus] ||
    rawStatus.replace("basic_tx.", "").replace(/_/g, " ");

  // Terminal failure states (surfaced to the buyer so a cancelled/expired
  // attempt doesn't vanish) must not read as a green "success" — show them
  // as a muted/danger chip so it's clear no payment went through.
  const FAILED_STATUSES: string[] = [
    PAYMENT_STATUS.CANCELLED,
    PAYMENT_STATUS.PAYMENT_FAILED,
    PAYMENT_STATUS.FUNDS_REFUNDED,
  ];
  if (FAILED_STATUSES.includes(rawStatus)) {
    return (
      <StatusChip
        className="statusChip--wide"
        tone={rawStatus === PAYMENT_STATUS.CANCELLED ? "muted" : "danger"}
        icon="bi-x-circle-fill"
        capitalize
        label={label}
      />
    );
  }

  return (
    <StatusChip
      className="statusChip--wide"
      tone="success"
      icon="bi-check-circle-fill"
      capitalize
      label={label}
    />
  );
};

export default PaymentStatusChip;
